type ThemeMode = 'light' | 'lightHighContrast' | 'dark';

type ScContext = { theme: { cbThemeMode?: ThemeMode } };

type ThemedValue = {
  dark: string;
  light: string;
  lightHighContrast?: string;
};

/**
 * Trims extra whitespace
 * ` a b      c` => ` a b c`
 */
const trimWhiteSpace = (str: string) => str.replace(/\s+/g, ' ');

/**
 * `cssThemedValue` is an utility to insert themed values in styled-components.
 *
 * Example:
 * ```tsx
 * const Root = styled.div`
 *   color: ${cssThemedValue({ light: colors.pepper60, dark: colors.salt72 })};
 * `;
 * ```
 *
 * For `lightHighContrast` mode, uses value mapped to `light` as fallback.
 *
 * Before, `cssThemedValue` supported also semantic color names as the argument.
 * This has functionality has been removed in favour of using color tokens from
 * `@creditornot/cb-ingredients/design-tokens`.
 */
export function cssThemedValue(value: ThemedValue) {
  return (context?: ScContext): string => {
    if (!context) {
      throw new Error(
        trimWhiteSpace(
          `Missing styled-components context. The cssThemedValue function must be used
           inside a styled-components component and ThemeProvider must be set up.`,
        ),
      );
    }

    if (!context.theme.cbThemeMode) {
      throw new Error('Missing `cbThemeMode` definition in styled-components theme.');
    }

    const cbThemeMode = context.theme.cbThemeMode;

    return cbThemeMode === 'lightHighContrast' && !value[cbThemeMode]
      ? value.light
      : (value[cbThemeMode] as string);
  };
}
